import { useState } from "react";
import { useTheme } from "next-themes";

import { Button } from "@/components/atoms/Button";
import { Card, CardContent } from "@/components/atoms/Card";
import Like from "@/components/atoms/Icon/like";
import Unlike from "@/components/atoms/Icon/unLike";
import { Paragraph } from "@/components/atoms/Paragraph";
import { Rating } from "@/components/atoms/Rating";
import { ReviewStatus } from "@/components/atoms/ReviewStatus";
import EditDeleteDropDown from "@/components/molecules/Review/editDeleteDropDown";
import type {
  ICurrentHelpfulOrUnhelpfulState,
  IReviewProps,
} from "@/components/molecules/Review/types";
import convertToIdo from "@/helpers/convertToIdo";
import { useAuthUser } from "@/hooks/common/useAuthUser";
import { useTranslations } from "@/hooks/common/useTranslations";

import { HelpfulOrUnhelpfulReviewEnum } from "@/common/globalTypes/enums/common";

const Review = ({
  review,
  reviewPoint,
  reviewHandlers: {
    helpfulOrUnhelpfulReviewHandler,
    onClickDeleteHandler,
    onClickEditHandler,
  },
}: IReviewProps) => {
  const { t } = useTranslations("SingleAppPage");

  const {
    id: reviewId,
    username,
    reviewTitle,
    review: feedback,
    createdAt,
    rate,
    userAction,
    helpfulCount,
    notHelpfulCount,
    userId,
  } = review;

  const { isLogged, user } = useAuthUser();
  const editMode = isLogged && user?.id === userId;

  const { theme } = useTheme();
  const { logIn } = useAuthUser();
  const [
    currentHelpfulOrUnhelpfulActionId,
    setCurrentHelpfulOrUnhelpfulActionId,
  ] = useState<ICurrentHelpfulOrUnhelpfulState | null>(null);

  const isSelectedHelpfulBtn =
    userAction === HelpfulOrUnhelpfulReviewEnum.helpful;

  const isSelectedUnHelpfulBtn =
    userAction === HelpfulOrUnhelpfulReviewEnum.unhelpful;

  const mostHelpfulReview =
    reviewPoint === HelpfulOrUnhelpfulReviewEnum.helpful;

  const notHelpfulReview =
    reviewPoint === HelpfulOrUnhelpfulReviewEnum.unhelpful;

  const existNotHelpfulOrHelpfulReview = mostHelpfulReview || notHelpfulReview;

  const reviewStatus = mostHelpfulReview ? "helpful" : "unhelpful";

  const isLoadingAfterActionHelpFull =
    currentHelpfulOrUnhelpfulActionId?.helpfulOrUnhelpful ===
      HelpfulOrUnhelpfulReviewEnum.helpful &&
    reviewId === currentHelpfulOrUnhelpfulActionId.reviewId;

  const isLoadingAfterActionUnhelpFull =
    currentHelpfulOrUnhelpfulActionId?.helpfulOrUnhelpful ===
      HelpfulOrUnhelpfulReviewEnum.unhelpful &&
    reviewId === currentHelpfulOrUnhelpfulActionId.reviewId;

  const getButtonType = (isSelected: boolean) => {
    if (isSelected) {
      return theme === "light" ? "secondary" : "outline";
    }

    return "ghost";
  };

  const helpFulOrUnhelpfulHandler = (
    helpfulOrUnhelpful: HelpfulOrUnhelpfulReviewEnum,
    isDeleteVote: boolean,
  ) => {
    logIn();

    setCurrentHelpfulOrUnhelpfulActionId({
      helpfulOrUnhelpful,
      reviewId,
    });
    helpfulOrUnhelpfulReviewHandler(
      reviewId,
      helpfulOrUnhelpful,
      isDeleteVote,
      () => setCurrentHelpfulOrUnhelpfulActionId(null),
    );
  };

  return (
    <Card className="dark:bg-secondary">
      <CardContent className="px-4 py-4">
        <div className="flex flex-row max-sm:flex-col justify-between gap-5 pb-4">
          <div className="flex flex-col justify-start gap-2">
            <div className="flex justify-between">
              <Paragraph size="small" fontWeight="medium">
                {username}
              </Paragraph>
              {editMode && (
                <div className="hidden max-sm:flex">
                  <EditDeleteDropDown
                    onEdit={() => onClickEditHandler(review)}
                    onDelete={() => onClickDeleteHandler(reviewId)}
                  />
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Rating
                readOnly
                style={{ maxWidth: 100 }}
                items={5}
                value={rate}
              />
              <Paragraph size="small" fontWeight="medium">
                {rate}
              </Paragraph>
            </div>
            <div className="flex justify-between">
              <Paragraph size="small" fontWeight="regular" type="secondary">
                {convertToIdo(createdAt)}
              </Paragraph>
              {existNotHelpfulOrHelpfulReview ? (
                <ReviewStatus
                  type={reviewStatus}
                  className="max-sm:flex hidden"
                />
              ) : (
                <div />
              )}
            </div>
          </div>
          <div className="flex justify-between items-start basis-[75%]">
            <div className="flex flex-col gap-2">
              <Paragraph size="base" fontWeight="medium">
                {reviewTitle}
              </Paragraph>
              <Paragraph size="small" fontWeight="regular" type="secondary">
                {feedback}
              </Paragraph>
            </div>
            {editMode && (
              <div className="max-sm:hidden">
                <EditDeleteDropDown
                  onEdit={() => onClickEditHandler(review)}
                  onDelete={() => onClickDeleteHandler(reviewId)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="pt-4">
          <div className="flex justify-between">
            {existNotHelpfulOrHelpfulReview ? (
              <ReviewStatus type={reviewStatus} className="max-sm:hidden" />
            ) : (
              <div />
            )}

            <div className="flex gap-2">
              <Button
                variant={getButtonType(isSelectedHelpfulBtn)}
                className="rounded-xl"
                disabled={isLoadingAfterActionHelpFull}
                onClick={() =>
                  helpFulOrUnhelpfulHandler(
                    HelpfulOrUnhelpfulReviewEnum.helpful,
                    isSelectedHelpfulBtn,
                  )
                }
              >
                <Like />
                <Paragraph size="small" fontWeight="medium">
                  <span className="max-sm:hidden">{t("helpful")} </span>
                  {t("count", {
                    reviewCount: `(${helpfulCount})`,
                  })}
                </Paragraph>
              </Button>
              <Button
                variant={getButtonType(isSelectedUnHelpfulBtn)}
                className="rounded-xl"
                disabled={isLoadingAfterActionUnhelpFull}
                onClick={() =>
                  helpFulOrUnhelpfulHandler(
                    HelpfulOrUnhelpfulReviewEnum.unhelpful,
                    isSelectedUnHelpfulBtn,
                  )
                }
              >
                <Unlike />
                <Paragraph size="small" fontWeight="medium">
                  <span className="max-sm:hidden">{t("notHelpful")} </span>
                  {t("count", {
                    reviewCount: `(${notHelpfulCount})`,
                  })}
                </Paragraph>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Review;
